import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  getProvider,
  listProviders,
  ProviderApiError,
  type ProviderApiRow,
  type ProviderListQuery,
  type ProviderListResponse,
  type ProviderStatusFilter,
} from '@/api/providers';

const DEFAULTS = {
  PAGE: 1,
  PAGE_SIZE: 10,
  SORT_BY: 'provider_name',
  ORDER: 'asc' as const,
} as const;

interface UseProvidersReturn {
  providers: ProviderApiRow[];
  pagination: ProviderListResponse['pagination'] | undefined;
  isLoading: boolean;
  error: string | null;
  filters: ProviderListQuery;
  setSearch: (v: string) => void;
  setStatus: (v: ProviderStatusFilter[]) => void;
  setRole: (v: number[]) => void;
  setPage: (v: number) => void;
  setSort: (sortBy: string, order: 'asc' | 'desc') => void;
  /**
   * Replace every filter at once, preserving the defaults for anything omitted.
   *
   * Needed for URL hydration, which has to restore filters *and* a page number
   * together. The individual setters each force `page: 1` by design, so
   * replaying `?role=4&page=2` through them would drop the page.
   */
  applyFilters: (next: ProviderListQuery) => void;
  refetch: () => void;
}

/**
 * @param initialFilters seeds the first render, so a caller hydrating from the
 * URL starts in sync with it. Without this the caller's state-to-URL effect runs
 * on mount with default filters and overwrites the very query it is meant to
 * restore — the two effects fire in the same commit, and the second sees state
 * from before the first one's update lands.
 */
export const useProviders = (initialFilters?: ProviderListQuery): UseProvidersReturn => {
  const [data, setData] = useState<ProviderListResponse | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<ProviderListQuery>({
    page: DEFAULTS.PAGE,
    pageSize: DEFAULTS.PAGE_SIZE,
    sortBy: DEFAULTS.SORT_BY,
    order: DEFAULTS.ORDER,
    ...initialFilters,
  });

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const result = await listProviders(filters);
      setData(result.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load providers');
      setData(undefined);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const setSearch = useCallback((search: string) => {
    setFilters((prev) => ({ ...prev, search, page: DEFAULTS.PAGE }));
  }, []);

  // An empty selection removes the key rather than sending `?status=`, which the
  // backend enum would reject. Same for `setRole` below.
  const setStatus = useCallback((status: ProviderStatusFilter[]) => {
    setFilters((prev) => {
      if (status.length === 0) {
        const { status: _removed, ...rest } = prev;
        return { ...rest, page: DEFAULTS.PAGE };
      }
      return { ...prev, status, page: DEFAULTS.PAGE };
    });
  }, []);

  const setRole = useCallback((role: number[]) => {
    setFilters((prev) => {
      if (role.length === 0) {
        const { role: _removed, ...rest } = prev;
        return { ...rest, page: DEFAULTS.PAGE };
      }
      return { ...prev, role, page: DEFAULTS.PAGE };
    });
  }, []);

  const setPage = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  }, []);

  const setSort = useCallback((sortBy: string, order: 'asc' | 'desc') => {
    setFilters((prev) => ({ ...prev, sortBy, order, page: DEFAULTS.PAGE }));
  }, []);

  // Deliberately does NOT force page 1 — the caller supplies the page it wants.
  // Unspecified fields fall back to defaults so a partial URL still yields a
  // complete, valid query.
  const applyFilters = useCallback((next: ProviderListQuery) => {
    setFilters({
      page: next.page ?? DEFAULTS.PAGE,
      pageSize: next.pageSize ?? DEFAULTS.PAGE_SIZE,
      sortBy: next.sortBy ?? DEFAULTS.SORT_BY,
      order: next.order ?? DEFAULTS.ORDER,
      ...(next.search ? { search: next.search } : {}),
      ...(next.status && (!Array.isArray(next.status) || next.status.length > 0)
        ? { status: next.status }
        : {}),
      ...(next.role && (!Array.isArray(next.role) || next.role.length > 0)
        ? { role: next.role }
        : {}),
    });
  }, []);

  const providers = useMemo(() => data?.data ?? [], [data]);
  const pagination = useMemo(() => data?.pagination, [data]);

  return {
    providers,
    pagination,
    isLoading,
    error,
    filters,
    setSearch,
    setStatus,
    setRole,
    setPage,
    setSort,
    applyFilters,
    refetch: fetchData,
  };
};

interface UseProviderReturn {
  provider: ProviderApiRow | undefined;
  isLoading: boolean;
  error: string | null;
  notFound: boolean;
  refetch: () => void;
}

export const useProvider = (partyId: string): UseProviderReturn => {
  const [provider, setProvider] = useState<ProviderApiRow | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);

  const fetchData = useCallback(async () => {
    if (!partyId) {
      setIsLoading(false);
      setNotFound(true);
      return;
    }
    try {
      setIsLoading(true);
      setError(null);
      setNotFound(false);
      const result = await getProvider(partyId);
      setProvider(result.data);
    } catch (e: unknown) {
      if (e instanceof ProviderApiError && e.status === 404) {
        setNotFound(true);
      } else {
        setError(e instanceof Error ? e.message : 'Failed to load provider');
      }
    } finally {
      setIsLoading(false);
    }
  }, [partyId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { provider, isLoading, error, notFound, refetch: fetchData };
};
